import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GlobalMessage, GlobalMessagePage, MessageType } from 'src/app/core/interfaces/store-configuration.interface';

import { tap } from 'rxjs/operators';
import { OverlayLoginComponent } from 'src/app/authentication/components/overlay-login/overlay-login.component';
import { ButtonLgComponent } from "src/app/components/button-lg/button-lg.component";
import { ShippingPolicyComponent } from 'src/app/components/footer/components/shipping-policy/shipping-policy.component';
import { WebstoreMessagesPanelComponent } from "src/app/components/webstore-messages-panel/webstore-messages-panel.component";
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { Consumer } from 'src/app/core/models/consumer.model';
import { Action, GoogleAnalyticsService } from 'src/app/core/services/analytics/google-analytics.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { GlobalMessagesService } from 'src/app/core/services/global-messages/global-messages.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CartData } from '../../../core/interfaces/cart.interface';
import { Product, createDefaultProduct } from '../../models/product.model';
import { PriceService } from '../../services/price.service';
import { AboutBiddingOverlayComponent } from '../about-bidding-overlay/about-bidding-overlay.component';
import { BiddingOverlayComponent } from '../bidding-overlay/bidding-overlay.component';
import { DeliveryEtdStatusComponent } from "../delivery-etd-status/delivery-etd-status.component";

@Component({
  standalone: true,
  selector: 'webstore-product-cart-configuration',
  templateUrl: './product-cart-configuration.component.html',
  styleUrls: ['./product-cart-configuration.component.scss'],
  imports: [CommonModule, RouterModule, ReactiveFormsModule, ButtonLgComponent, DeliveryEtdStatusComponent, WebstoreMessagesPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductCartConfigurationComponent implements OnInit {
  route = inject(ActivatedRoute);
  router = inject(Router);
  overlayService = inject(OverlayService);
  cartRepositoryService = inject(CartRepositoryService);
  priceService = inject(PriceService);
  platformService = inject(PlatformService);
  globalMessagesService = inject(GlobalMessagesService);
  private userService = inject(UserService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private notificationService = inject(NotificationService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);

  constructor() {
    effect(() => {
      const productValue = this.productInput();
      if (productValue) {
        this._product = productValue;
        const { actualPrice, sellingPrice } = this.priceService.getProductPrice(productValue);
        this._sellingPrice = sellingPrice;
        this._actualPrice = actualPrice;
      }
    });
  }


  IMAGE_URLS = IMAGE_URLS;

  _sellingPrice: number = 0;
  _percentSavings: number = 0;
  _actualPrice: number = 0;

  loginRef!: MyOverlayRef<OverlayLoginComponent>;
  biddingRef!: MyOverlayRef<BiddingOverlayComponent, { productId: string, selling_price: number }, { bid_price: number }>;



  readonly productInput = input<Product>(createDefaultProduct());
  readonly errorMessage = input<string>('');
  readonly showEstimatedDelivery = input<boolean>(false);

  // Getters for backward compatibility
  get product(): Product {
    return this._product;
  }

  private _product: Product = createDefaultProduct();

  user: Consumer | null = this.userService.appUser?.user || null;
  isBrowser = this.platformService.isBrowser();
  buttonLoading = false;
  globalMessage: GlobalMessage = { type: MessageType.Information, message: '' };
  readonly showProductBidding = computed(() => this.storeConfigurationRepositoryService.showProductBidding());


  // Update outputs to use Angular 20 output() function
  addToCart$ = output<CartData>();
  buyNow$ = output<Product>();
  refetchProduct$ = output<true>();

  ngOnInit(): void {
    const globalMessage = this.getProductsGlobalMessage();
    this.globalMessage = globalMessage || { type: MessageType.Information, message: '' };
  }

  routeToProduct(url_path: string) {
    this.router.navigateByUrl(`/products/${url_path}`);
  }

  addToCart(product: Product, bid_price = 0) {
    this.addToCart$.emit({
      product,
      bid_price,
      productDetailOverlay: false,
      stayOnCurrentPage: true
    });
  }

  /* buyNow(product: Product) {
    const user = this.userService.appUser;
    if (user) {
      this.buyNow$.next(product);
    } else {
      // login flow
      this.loginRef = this.overlayService.open(OverlayLoginComponent, null);
      this.loginRef.afterClosed$.pipe(
        pluck('data', 'success'),
        take(1)
      ).subscribe((success: LoginSuccess) => {
        if (success) {
          this.buyNow$.next(product);
          this.refetchProduct$.next(true);
        }
      });
    }
  } */

  /**
   * 
   * @returns the global message for the product
   * the priority has been given to the product configured global message
   * if there is no global message configured for product then sales channel configured global message will be considered
   */
  getProductsGlobalMessage() {
    return this.product?.global_message ? {
      type: MessageType.Information,
      message: this.product.global_message
    } : this.globalMessagesService.getGlobalMessage(GlobalMessagePage.ProductDetailPage);
  }

  openExchangePolicy() {
    this.overlayService.open(ShippingPolicyComponent, null, true, 'centered');
  }

  openBiddingOverlay() {
    const user = this.userService.appUser;
    if (!user?.access_token) {
      this.router.navigate(['/auth/login-otp'], {
        queryParams: {
          returnUrl: `products/${this.route.snapshot.url}`
        }
      });
      this.notificationService.showInfo('Please log in to your account to continue.');
    } else {
      this.googleAnalyticsService.injectScript({ action: Action.BidInitiated })
      this.biddingRef = this.overlayService.open<BiddingOverlayComponent, { productId: string, selling_price: number }>(BiddingOverlayComponent, {
        productId: this.product?.id || '',
        selling_price: this._sellingPrice
      }, true, 'centered', '', 'block-scroll');

      this.biddingRef.receiveData$.pipe(
        tap((data) => this.addToCart(this.product!, data.data.bid_price))
      ).subscribe(() => {
        this.biddingRef.close();
      });
    }
  }

  openAboutBiddingOverlay() {
    this.overlayService.open(AboutBiddingOverlayComponent, null, true, 'centered');
  }

  get productId(): string {
    return this.product?.id || '';
  }

  get productUrlPath(): string {
    return this.product?.url_path || '';
  }

  get productImages(): string[] {
    return this.product?.images || [];
  }
}

